import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, Toggle, TopBar, Button } from "@/components/ui";
import { useConsent } from "@/hooks/useConsent";
import { usePushToken } from "@/hooks/usePushToken";
import { useAuth } from "@/store/auth";
import type { ConsentStatus } from "@/api/consent-types";

function on(s: ConsentStatus | undefined): boolean {
  return s === "opted_in";
}

export function PreferenceCenterScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { consent, loading, saving, error, setChannel, withdrawAll, reload } = useConsent();
  const { enablePush, disablePush, busy } = usePushToken();

  const hasEmail = !!user?.email;

  async function togglePush(next: boolean) {
    if (next) {
      const ok = await enablePush();
      if (ok) await setChannel("push", "opted_in");
    } else {
      await setChannel("push", "opted_out");
      await disablePush();
    }
  }

  return (
    <div style={{ paddingBottom: 80 }}>
      <TopBar title={t("v2.preferences.title")} onBack={() => navigate("/profile")} />
      <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.45 }}>
          {t("v2.preferences.subtitle")}
        </div>

        {loading && <Card padding={16}>…</Card>}
        {error && <Card padding={16} style={{ color: "var(--danger-600)" }}>{t("v2.preferences.load_error")}</Card>}

        {consent && (
          <Card padding={0}>
            <Row label={t("v2.preferences.channel_whatsapp")} on={on(consent.whatsapp.status)}
                 disabled={saving} onChange={(v) => setChannel("whatsapp", v ? "opted_in" : "opted_out")} />
            <Row label={t("v2.preferences.channel_email")} on={on(consent.email.status)}
                 disabled={saving || !hasEmail} hint={!hasEmail ? t("v2.preferences.email_needed") : undefined}
                 onChange={(v) => setChannel("email", v ? "opted_in" : "opted_out")} />
            <Row label={t("v2.preferences.channel_push")} on={on(consent.push.status)}
                 disabled={saving || busy} onChange={togglePush} />
            <Row label={t("v2.preferences.channel_sms")} on={on(consent.sms.status)}
                 disabled={saving} onChange={(v) => setChannel("sms", v ? "opted_in" : "opted_out")} />
          </Card>
        )}

        {consent && (
          <Button variant="ghost" onClick={async () => { await withdrawAll(); await reload(); }}>
            {t("v2.preferences.withdraw_all")}
          </Button>
        )}
      </div>
    </div>
  );
}

function Row({
  label, hint, on, disabled, onChange,
}: { label: string; hint?: string; on: boolean; disabled?: boolean; onChange: (v: boolean) => void }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderTop: "1px solid var(--border)" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-1)" }}>{label}</div>
        {hint && <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>{hint}</div>}
      </div>
      <Toggle on={on} onChange={() => !disabled && onChange(!on)} />
    </div>
  );
}
